import { TimeInfo } from '../preview-proposal/proposal/proposal-count-down';
import ConfirmDefaultModal from '../../../../global/modal/confirm-default-modal';
import { useExtensionActions } from '../../../../../hooks/actions/use-extension-actions';
import { useAuthDataState } from '../../../../../hooks/data-state/use-auth-data-state';

interface Props {
    timeLeft: TimeInfo;
}

export default function ProposalPreviewRequestExtension({ timeLeft }: Props) {
    
    const { isUserMonitoring } = useAuthDataState();
    const { 
        isLoadingRequestProposalExtension,
        handleOpenConfirmation,
        handleCancelConfirmation,
        handleRequestExtension,
        showConfirmModal,
        isRequestPending,
        isSubmitting,
        showRequestExtension,
    } = useExtensionActions();
    const isShowRequestExtension = showRequestExtension && (timeLeft.days <= 5 || timeLeft.expired)

    const getTimeMessage = () => {
        if (timeLeft.expired) {
            return 'The proposal has expired.';
        }
        if (timeLeft.days === 0) {
            return 'The proposal expires today.';
        }
        if (timeLeft.days === 1) {
            return 'The proposal expires tomorrow.';
        }
        return `Only ${timeLeft.days} days remaining before expiration.`;
    };

    if (!isShowRequestExtension) return null;

    return (
        <>
            <div className="rounded-md border border-gray-200 bg-white">
                <div className="flex min-w-0 items-center gap-2 p-2.5 sm:p-3">
                    {/* ICON */}
                    <div className={`border border-[#186cb2]/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md`}>
                        {isRequestPending ? (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 text-amber-600" >
                                <circle cx="12" cy="12" r="9" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 text-[#186cb2]" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                            </svg>
                        )}
                    </div>
                    {/* CONTENT */}
                    <div className="min-w-0 flex-1">
                        <p className={`text-[11px] font-semibold text-[#186cb2]`}>
                            {isRequestPending
                                ? 'Extension request pending'
                                : 'Request for extension?'}
                        </p>
                        <p className={`truncate text-[10px] text-gray-500`}>
                            {getTimeMessage()}
                        </p>
                    </div>
                    {/* ACTION */}
                    {isRequestPending ? (
                        <div
                            className="flex h-8 shrink-0 items-center gap-1 rounded-md px-2 text-green-600"
                            title="Extension request is pending"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
                            </svg>
                            <span className="text-[10px] font-semibold">
                                Pending
                            </span>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => {
                                if(isUserMonitoring) return;
                                handleOpenConfirmation()
                            }}
                            disabled={isSubmitting || isUserMonitoring}
                            title="Request extension"
                            className={`${isSubmitting ? '' : 'border border-[#186cb2]/50 hover:border-[#186cb2]'} flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-[#186cb2] transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#186cb2]/20`}
                        >
                            {isSubmitting ? (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 animate-spin" >
                                    <circle cx="12" cy="12" r="9" className="opacity-25" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3a9 9 0 0 1 9 9" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m13 6 6 6-6 6" />
                                </svg>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* CONFIRMATION MODAL */}
            {showConfirmModal && (
                <ConfirmDefaultModal
                    modalTitle="Request Extension?"
                    modalSubTitle={`This proposal ${timeLeft.expired ? 'has already expired' : `has only ${timeLeft.days} ${timeLeft.days === 1 ? 'day' : 'days'} remaining`}. Are you sure you want to request an extension?`}
                    onCancel={handleCancelConfirmation}
                    onConfirm={handleRequestExtension}
                    isLoading={isLoadingRequestProposalExtension}
                />
            )}
        </>
    );
}